/**
 * RISK-SUGGESTION-MODAL
 * Display AI suggestions with Before/After comparison
 */
import Modal from "@/components/modal"
import { t } from "i18next"
import { faSync, faPencil } from "@fortawesome/free-solid-svg-icons"
import ListButton from "@/components/list-button"
import Space from "@/components/space"
import Button from "@/components/button"
import { OwlRunner } from "@/components/OwlRunner"
import { useState, useEffect } from "react"

type Props = {
  field: string
  oldValue: string
  newValue: string
  onAccept: (value: string) => void
  onReject: () => void
  onReroll: () => void
  isLoading?: boolean
  suggestionId?: number
}

export default function RiskSuggestionModal({
  field,
  oldValue,
  newValue,
  onAccept,
  onReject,
  onReroll,
  isLoading = false,
  suggestionId
}: Props) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedValue, setEditedValue] = useState(newValue)
  const [isRerolling, setIsRerolling] = useState(false)

  useEffect(() => {
    setEditedValue(newValue)
    setIsRerolling(false)
    setIsEditing(false)
  }, [field, newValue, suggestionId])

  function handleAccept() {
    if (isRerolling) return
    onAccept(editedValue)
    setIsEditing(false)
  }

  function handleEditToggle() {
    if (isRerolling) return
    if (isEditing) {
      setEditedValue(newValue)
    }
    setIsEditing(!isEditing)
  }

  function handleReroll() {
    if (isRerolling) return
    setIsRerolling(true)
    onReroll()
  }

  function handleReject() {
    if (isRerolling) return
    onReject()
  }

  return (
    <Modal
      title={`Suggestion pour « ${field} »`}
      className="max-w-2xl"
      onClose={handleReject}
      footer={
        <div className="flex gap-2 items-center">
          <Button isWithBorder onClick={handleReject} isLoading={isLoading || isRerolling}>
            {t("utils_cancel")}
          </Button>
          <Space />
          <Button className="primary" onClick={handleAccept} isLoading={isLoading || isRerolling}>
            {t("utils_accept")}
          </Button>
        </div>
      }>
      <div className="flex flex-col gap-6">
        {/* Avant */}
        <div>
          <div className="text-sm font-semibold text-gray-600 mb-2">Avant</div>
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 min-h-20">
            {oldValue ? (
              <p className="text-gray-700 whitespace-pre-wrap">{oldValue}</p>
            ) : (
              <p className="text-gray-400 italic">Aucune valeur</p>
            )}
          </div>
        </div>

        {/* Après */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-gray-600">Après</div>
            <div className="flex gap-2">
              <ListButton
                icon={faPencil}
                text={isEditing ? "Annuler" : "Réécrire"}
                tooltipPosition="left"
                onClick={handleEditToggle}
                isPrimary
                isInactive={isLoading || isRerolling}
              />
              <ListButton
                icon={faSync}
                text="Réessayer"
                tooltipPosition="left"
                onClick={handleReroll}
                isInactive={isLoading || isRerolling}
              />
            </div>
          </div>

          {isEditing ? (
            <div className="relative">
              <textarea
                className="text-area w-full p-3 rounded-lg border border-blue-200 focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                value={editedValue}
                onChange={(e) => setEditedValue(e.target.value)}
                rows={4}
                disabled={isRerolling}
              />
              {isRerolling && (
                <div className="absolute bottom-2 right-2">
                  <OwlRunner loading={true} />
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 min-h-20 relative">
              <p className="text-gray-700 whitespace-pre-wrap">{editedValue}</p>
              {isRerolling && (
                <div className="absolute bottom-2 right-2">
                  <OwlRunner loading={true} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
